/**
 * apps/battle/client/src/terminal/screens/PortalEntryScreen.tsx
 * Portal entry narrative. Auto-advances after 8 s or on [Enter].
 */
import { useEffect, useState } from 'react';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';
import { useKeyboard } from '../hooks/useKeyboard.js';
import { PORTAL_NARRATIVE, type PortalKey } from '../narrative.js';
import { NARRATIVE_AUTO_ADVANCE_MS } from '../config.js';
import type { PortalState } from '../../api.js';

interface Props {
  portal: PortalState;
  onBegin: () => void;
  onBackToTitle: () => void;
}

export function PortalEntryScreen({ portal, onBegin, onBackToTitle }: Props) {
  const n = PORTAL_NARRATIVE[portal.portalKey as PortalKey];
  const [countdown, setCountdown] = useState(Math.round(NARRATIVE_AUTO_ADVANCE_MS / 1000));

  useEffect(() => {
    const t = setTimeout(onBegin, NARRATIVE_AUTO_ADVANCE_MS);
    const i = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => { clearTimeout(t); clearInterval(i); };
  }, [onBegin]);

  useKeyboard([
    { key: 'Enter', handler: onBegin },
    { key: 'b', handler: onBackToTitle },
  ]);

  const title = (
    <>
      ECONQUEST <span className="tm-text--dim">{'\u2500'}</span>{' '}
      <span className="tm-text--portal">PORTAL {portal.endpointA} {'\u2194'} {portal.endpointB}</span>{' '}
      <span className="tm-text--dim">{'\u2500'}</span>{' '}
      <span className="tm-text--accent">ENTERING</span>
    </>
  );

  const keybar: KeyBarItem[] = [
    { label: 'Enter', description: 'begin', onActivate: onBegin },
    { label: 'B', description: 'back to title', onActivate: onBackToTitle },
  ];

  return (
    <TerminalFrame title={title} keybar={<KeyBar items={keybar} />}>
      <div className="tm-narrative">
        <div className="tm-h1 tm-text--portal">
          {'\u26A1'} {n?.name ?? `${portal.endpointA} \u2194 ${portal.endpointB}`}
        </div>
        {n?.entry ?? `A bridge of light connects Cluster ${portal.endpointA} and Cluster ${portal.endpointB}.`}
        <div className="tm-narrative__sub">
          {portal.tier}-mark {portal.isPermanent ? 'permanent' : 'conditional'} portal {'\u00B7'} pass bar {'\u2265'} {portal.passBar}
        </div>
        <div className="tm-narrative__sub">
          {'>'} auto-advance in {countdown}s
        </div>
      </div>
    </TerminalFrame>
  );
}
